import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  reauthenticateWithPopup,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  updateEmail,
  verifyBeforeUpdateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  applyActionCode as firebaseApplyActionCode,
  checkActionCode as firebaseCheckActionCode
} from 'firebase/auth';
import { auth } from './config';
import { DEMO_MODE } from '@demo/config';
import { demoFirebaseUser } from '@demo/user';

// Helper to get API base URL (same logic as http-common.js)
const getApiBaseUrl = () => {
  const API_PORT = process.env.VUE_APP_API_PORT || '8080';
  const isDevelopment = process.env.NODE_ENV === 'development' || 
    (typeof window !== 'undefined' && window.location.hostname === 'localhost');
  return isDevelopment 
    ? `http://localhost:${API_PORT}`
    : 'https://inr-backend-604839735443.northamerica-northeast1.run.app';
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return {
      success: true,
      user: userCredential.user
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

/**
 * Sign in with Google
 */
export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return {
      success: true,
      user: result.user
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

/**
 * Sign out
 */
export const signOutUser = async () => {
  if (DEMO_MODE) {
    // Signing out of a demo would strand the visitor on a sign-in screen they
    // cannot pass. Restarting the demo is the honest equivalent.
    window.location.reload();
    return { success: true };
  }
  try {
    await signOut(auth);
    return {
      success: true
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

/**
 * Send password reset email (using backend API for custom branding)
 */
export const resetPassword = async (email) => {
  try {
    const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:8080';
    const response = await fetch(`${apiUrl}/api/auth/request-password-reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });
    
    const data = await response.json();
    
    return {
      success: data.success || true,
      message: data.message
    };
  } catch (error) {
    return {
      success: false,
      error: error.code || 'network-error',
      message: error.message || 'Failed to send password reset email'
    };
  }
};

/**
 * Resend verification email
 */
export const resendVerificationEmail = async () => {
  try {
    const user = getCurrentUser();
    if (!user) {
      return {
        success: false,
        message: 'No user is currently signed in.'
      };
    }
    
    if (user.emailVerified) {
      return {
        success: false,
        message: 'Email is already verified.'
      };
    }
    
    await sendEmailVerification(user);
    return {
      success: true
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

/**
 * Listen to auth state changes
 */
export const onAuthStateChange = (callback) => {
  if (DEMO_MODE) {
    // One user, already signed in, forever. Async so callers that register a
    // listener during setup() still see it fire after they finish mounting,
    // exactly as the SDK does.
    Promise.resolve().then(() => callback(demoFirebaseUser));
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
};

/**
 * Get current user
 */
export const getCurrentUser = () => {
  if (DEMO_MODE) return demoFirebaseUser;
  return auth.currentUser;
};

/**
 * Resolves once Firebase has finished restoring a session (or decided there is
 * none). `auth.currentUser` is null until then.
 *
 * WHY THIS IS EXPORTED
 * --------------------
 * Restoring a session is asynchronous — persistence is read from IndexedDB, and
 * config.js additionally calls setPersistence() after getAuth(). Anything that
 * reads `getCurrentUser()` during that window sees null and cannot tell "signed
 * out" from "not restored yet". For the HTTP layer those two are very
 * different: the first is a request that should go out unauthenticated, the
 * second is a request that must WAIT, because sending it now produces a 401 —
 * which every screen that fetches on mount renders as a failure, for a user who
 * is signed in. The route guard already waits before it renders a guarded
 * route, so this closes the remaining window: anything that fetches outside a
 * navigation (a store warming up, a poll, an unguarded route).
 *
 * The router keeps its own listener for the same reason and predates this one;
 * it also collects role claims, so the two are not folded together here.
 *
 * The timeout is the safety valve: a misconfigured Firebase project would
 * otherwise leave every request pending forever, which is a worse failure than
 * the 401 this exists to prevent. Resolving late is fine — callers only ever
 * await it once.
 */
const AUTH_READY_TIMEOUT_MS = 5000;

export const authReady = DEMO_MODE ? Promise.resolve(demoFirebaseUser) : new Promise((resolve) => {
  let settled = false;
  const settle = (user) => {
    if (settled) return;
    settled = true;
    resolve(user || null);
  };

  const timer = setTimeout(() => {
    console.warn('[auth] Firebase auth state did not settle within 5s; proceeding without it');
    settle(null);
  }, AUTH_READY_TIMEOUT_MS);

  onAuthStateChanged(auth, (user) => {
    clearTimeout(timer);
    settle(user);
  });
});

/**
 * Sign in with custom token (for magic links)
 */
export const signInWithCustomToken = async (customToken) => {
  try {
    const userCredential = await firebaseSignInWithCustomToken(auth, customToken);
    
    // The user should already have email and displayName set by the backend
    // when the Firebase Auth user was created, so no need to update here
    console.log('✅ Signed in with custom token:', {
      uid: userCredential.user.uid,
      email: userCredential.user.email,
      displayName: userCredential.user.displayName
    });
    
    return {
      success: true,
      user: userCredential.user
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

/**
 * Convert Firebase error codes to user-friendly messages
 */
const getErrorMessage = (errorCode) => {
  const errorMessages = {
    'auth/email-already-in-use': 'This email address is already in use by another account.',
    'auth/invalid-email': 'Invalid email address.',
    'auth/operation-not-allowed': 'Email change is not enabled. Please contact support.',
    'auth/weak-password': 'Password should be at least 10 characters.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/popup-closed-by-user': 'Sign in popup was closed.',
    'auth/cancelled-popup-request': 'Sign in cancelled.',
    'auth/expired-action-code': 'This link has expired. Please request a new one.',
    'auth/invalid-action-code': 'This link is invalid or has already been used.',
    'auth/requires-recent-login': 'For security, please sign out and sign back in before changing your email.',
    'auth/invalid-new-email': 'The new email address is invalid.',
    'auth/user-token-expired': 'Your session has expired. Please sign in again.',
  };
  
  return errorMessages[errorCode] || `An error occurred (${errorCode || 'unknown'}). Please try again.`;
};

/**
 * Send email change verification via the backend API.
 *
 * The backend forwards it to the Praxio email API, which renders the shared
 * branded template and delivers via Resend — this client never talks to a mail
 * provider itself.
 */
export const sendEmailChangeVerification = async (newEmail, firstName = '', lastName = '', lang = 'en') => {
  const user = getCurrentUser();
  if (!user) {
    return {
      success: false,
      error: 'no-user',
      message: 'No user is currently signed in.'
    };
  }

  try {
    
    const apiUrl = getApiBaseUrl();

    // The endpoint now requires a verified Firebase ID token and derives the
    // caller's identity (userId + current email) from it server-side.
    const idToken = await user.getIdToken();

    const response = await fetch(`${apiUrl}/api/auth/request-email-change`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        newEmail,
        firstName,
        lastName,
        lang
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      console.error('Email change request failed:', result);
      return {
        success: false,
        error: result.error || 'request-failed',
        message: result.message || 'Failed to send verification email'
      };
    }

    
    // In development, log the verification link for testing
    if (result.verificationLink) {
    }
    
    return {
      success: true,
      message: result.message || 'Verification email sent to the new address. Please check your inbox and click the link to confirm.'
    };
    
  } catch (error) {
    console.error('Email change request error:', error);
    return {
      success: false,
      error: 'network-error',
      message: 'Failed to connect to server. Please try again.'
    };
  }
};

/**
 * Verify email change token and complete the change
 * Called from AuthAction.vue when user clicks the verification link
 */
export const verifyEmailChange = async (token) => {
  try {
    
    const apiUrl = getApiBaseUrl();
    
    const response = await fetch(`${apiUrl}/api/auth/verify-email-change`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token })
    });

    const result = await response.json();
    
    if (!response.ok) {
      console.error('Email change verification failed:', result);
      return {
        success: false,
        error: result.error || 'verification-failed',
        message: result.message || 'Failed to verify email change'
      };
    }

    
    // Sign out the user (their session is invalidated anyway)
    await signOutUser();
    
    return {
      success: true,
      message: result.message || 'Email changed successfully. Please sign in with your new email.',
      newEmail: result.newEmail
    };
    
  } catch (error) {
    console.error('Email change verification error:', error);
    return {
      success: false,
      error: 'network-error',
      message: 'Failed to connect to server. Please try again.'
    };
  }
};

/**
 * Cancel pending email change
 */
export const cancelPendingEmailChange = async () => {
  const user = getCurrentUser();
  if (!user) {
    return { success: false, error: 'no-user' };
  }

  try {
    const apiUrl = getApiBaseUrl();
    
    const response = await fetch(`${apiUrl}/api/auth/cancel-email-change`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId: user.uid })
    });

    return await response.json();
  } catch (error) {
    console.error('Cancel email change error:', error);
    return { success: false, error: 'network-error' };
  }
};

/**
 * Apply action code (for email verification)
 */
export const applyActionCode = async (actionCode) => {
  return await firebaseApplyActionCode(auth, actionCode);
};

/**
 * Check action code validity
 */
export const checkActionCode = async (actionCode) => {
  return await firebaseCheckActionCode(auth, actionCode);
};
